import { useState, useRef } from 'react';
import { Upload, AlertCircle, FileText, File } from 'lucide-react';
import { useFailSafeUpload, type UploadResult } from '../hooks/useFailSafeUpload';
import { getFileTypeLabel } from '../lib/extractDocumentContent';
import UploadStatusModal from './UploadStatusModal';
import UploadedDocumentSummary from './UploadedDocumentSummary';

interface FileUploadParserV2Props {
  onUploadComplete?: (content: string, fileName: string, analysis: any) => void;
}

export default function FileUploadParserV2({ onUploadComplete }: FileUploadParserV2Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [uploadedResult, setUploadedResult] = useState<UploadResult | null>(null);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const upload = useFailSafeUpload({
    onStageUpdate: () => {
      // Update modal in real-time
    },
    onSuccess: (result) => {
      setUploadedResult(result);
      setShowSummary(true);
      if (onUploadComplete && result.success) {
        onUploadComplete(result.content, result.fileName, result.analysis);
      }
    },
    onError: (error) => {
      console.error('Upload error:', error);
    },
  });

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      startUpload(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      startUpload(files[0]);
    }
  };

  const startUpload = async (file: File) => {
    setFileToUpload(file);
    setShowModal(true);
    setShowSummary(false);
    
    // Simulate upload progress first
    await upload.simulateUploadProgress(file);
    
    // Then process the file with retry logic
    await upload.processFileWithRetry(file, 3);
  };

  const handleImport = () => {
    if (uploadedResult?.success) {
      onUploadComplete?.(uploadedResult.content, uploadedResult.fileName, uploadedResult.analysis);
      resetUpload();
    }
  };

  const handleRetry = async () => {
    if (fileToUpload) {
      upload.reset();
      setShowSummary(false);
      await startUpload(fileToUpload);
    }
  };

  const resetUpload = () => {
    upload.reset();
    setUploadedResult(null);
    setShowSummary(false);
    setShowModal(false);
    setFileToUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full h-full space-y-4 flex flex-col overflow-y-auto">
      {/* Upload Area */}
      {!showSummary && (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 flex-shrink-0 ${
            isDragging
              ? 'border-white/60 bg-white/10 backdrop-blur-md'
              : 'border-white/30 bg-white/5 hover:bg-white/8 hover:border-white/40'
          }`}
        >
          <div className="flex flex-col items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
              <Upload className="w-8 h-8 text-white/80" />
            </div>
            <div>
              <p className="text-white font-semibold">Drag & drop your document here</p>
              <p className="text-white/60 text-sm mt-1">or click to browse</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs text-white/50 mb-4">
              <div className="flex items-center gap-1 justify-center">
                <File className="w-3 h-3" /> PDF, Word, Excel
              </div>
              <div className="flex items-center gap-1 justify-center">
                <FileText className="w-3 h-3" /> PowerPoint, HTML, RTF
              </div>
            </div>
            <p className="text-white/40 text-xs border-t border-white/10 pt-3">
              ✓ .docx files fully supported with formatting preservation
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInput}
            className="hidden"
            accept=".pdf,.doc,.docx,.xlsx,.xls,.pptx,.ppt,.html,.rtf,.txt"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="mt-4 px-6 py-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white rounded-lg transition-all duration-200 font-medium"
          >
            Select File
          </button>
        </div>
      )}

      {/* Document Summary */}
      {showSummary && uploadedResult?.success && (
        <div className="flex-1 overflow-y-auto">
          <UploadedDocumentSummary
            file={uploadedResult.file}
            analysis={{
              ...uploadedResult.analysis,
              wordCount: uploadedResult.wordCount,
            }}
            content={uploadedResult.content}
            onReset={resetUpload}
            onImport={handleImport}
          />
        </div>
      )}

      {/* Upload Status Modal */}
      <UploadStatusModal
        isOpen={showModal}
        onClose={() => {
          if (!upload.isUploading && !upload.isProcessing) {
            setShowModal(false);
          }
        }}
        isUploading={upload.isUploading}
        isProcessing={upload.isProcessing}
        uploadProgress={upload.uploadProgress}
        processingStages={upload.processingStages}
        currentError={upload.currentError}
        uploadResult={uploadedResult}
        fileName={fileToUpload?.name || 'Document'}
        fileType={fileToUpload ? getFileTypeLabel(fileToUpload.name) : 'Unknown'}
        onRetry={handleRetry}
      />
    </div>
  );
}
